/* eslint-disable import/extensions */

import React from 'react'
import { useSelector } from 'react-redux'

import { RadioGroup, KeyboardDatePicker, TextField } from '@masfront/common-b2c'
import { subYears } from 'date-fns'
import { Field, Form, FastField, Formik } from 'formik'

import {
  Box,
  Divider,
  FormControlLabel,
  FormHelperText,
  makeStyles,
  MenuItem,
  Paper,
  Radio,
  Typography,
  Collapse,
  Fade,
  InputAdornment,
  Grid,
} from '@material-ui/core'

import customerInfoSchema from './CustomerInfoForm.schemas'
import useCustomerInfoForm from './useCustomerInfoForm'

import useClientExists from '../../hooks/useClientExists'
import useClientFieldValues from '../../hooks/useClientFieldValues'
import { DocType } from '../../model/customer.model'
import { selectCustomerInfoFormValues } from '../../store/customer.selectors'

import { RootState } from '~/core/store/root.reducer'
import useMasterData from '~/masterData/hooks/useMasterData'
import { selectIsPostpaid } from '~/sale/store/sale.selectors'
import { LineTypes } from '~/tariff/model/tariff.model'
import { selectHasSelectedTariff } from '~/tariff/store/tariff.selectors'

// TODO: Reuse this styles without generating problems
const useStyles = makeStyles(() => ({
  fieldset: {
    border: 'none',
    '&:disabled': {
      pointerEvents: 'none',
    },
    margin: 0,
    padding: 0,
  },

  root: {
    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& .MuiInputBase-root': {
      '& input': {
        textTransform: 'capitalize',
      },
    },
  },
  upper: {
    '& .MuiInputBase-root': {
      '& input': {
        textTransform: 'uppercase',
      },
    },
  },
}))

function CustomerForm() {
  const styles = useStyles()
  const hasSelectedTariff = useSelector((state: RootState) =>
    selectHasSelectedTariff(state, LineTypes.MAIN),
  )

  const { nationalities, segments } = useMasterData()
  const { customerSegment, segmentError, isCompanySegment, docType } = useCustomerInfoForm()
  const isPostpaid = useSelector(selectIsPostpaid)

  const schema = customerInfoSchema(isPostpaid)

  useClientExists()
  const { checkExistentCustomerValues } = useClientFieldValues()

  return (
    <Form noValidate>
      <Box display="flex" flexDirection="row" py={4} alignItems="center">
        <Box mr={4}>
          <Typography>Selecciona el tipo de cliente:</Typography>
        </Box>
        <Field component={RadioGroup} name="customerSegment" error={segmentError}>
          <Box display="flex" flexWrap="wrap" alignItems="center">
            {segments?.map(({ id, value }) => (
              <FormControlLabel
                key={`segment-${id}`}
                value={value}
                control={
                  <Radio
                    disabled={
                      (!hasSelectedTariff && !isPostpaid) ||
                      checkExistentCustomerValues('customerSegment')
                    }
                    color="primary"
                    data-hook={value}
                  />
                }
                label={value}
              />
            ))}
            <FormHelperText error>{segmentError}</FormHelperText>
          </Box>
        </Field>
      </Box>
      <Divider light />
      <Box my={6}>
        <Typography variant="subtitle2" color="textSecondary">
          DATOS {isCompanySegment ? 'DEL ADMINISTRADOR' : 'PERSONALES'}
        </Typography>
      </Box>
      <fieldset disabled={!customerSegment} className={styles.fieldset}>
        <Grid container component={Box} spacing={5}>
          <Grid xs={3} item>
            <Field
              disabled={checkExistentCustomerValues('docId')}
              component={TextField}
              className={styles.upper}
              name="docId"
              label="Documento"
              variant="outlined"
              inputProps={{
                maxLength: 9,
              }}
              InputProps={{
                'data-blocker': true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography data-hook="docType" variant="caption" color="textSecondary">
                      {docType}
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid xs={3} item>
            <Field
              disabled={
                !customerSegment ||
                !docType ||
                docType === DocType.NIF ||
                checkExistentCustomerValues('nationality')
              }
              component={TextField}
              name="nationality"
              variant="outlined"
              label="Nacionalidad"
              select
            >
              {nationalities?.map(({ id, value }) => (
                <MenuItem value={value} key={`country-${id}`}>
                  {value}
                </MenuItem>
              ))}
            </Field>
          </Grid>
          <Fade in={!isCompanySegment} unmountOnExit>
            <Grid xs={3} item>
              <Field
                disabled={checkExistentCustomerValues('gender')}
                component={TextField}
                name="gender"
                variant="outlined"
                label="Género"
                select
              >
                <MenuItem value="F">Femenino</MenuItem>
                <MenuItem value="M">Masculino</MenuItem>
              </Field>
            </Grid>
          </Fade>
        </Grid>
        <Divider component={Box} className="dotted" pb={4} />
        <Grid container component={Box} py={4} spacing={5}>
          <Grid xs={4} item>
            <FastField
              disabled={checkExistentCustomerValues('name')}
              component={TextField}
              name="name"
              label="Nombre"
              variant="outlined"
            />
          </Grid>
          <Grid xs={4} item>
            <FastField
              disabled={checkExistentCustomerValues('firstName')}
              component={TextField}
              name="firstName"
              label="Apellido 1"
              variant="outlined"
            />
          </Grid>
          <Grid xs={4} item>
            <FastField
              disabled={checkExistentCustomerValues('lastName')}
              component={TextField}
              name="lastName"
              label="Apellido 2"
              variant="outlined"
            />
          </Grid>
        </Grid>
        <Grid container component={Box} spacing={5}>
          <Fade in={!isCompanySegment} unmountOnExit>
            <Grid xs={5} item>
              <Field
                disabled={checkExistentCustomerValues('birthdate')}
                component={KeyboardDatePicker}
                initialFocusedDate={subYears(new Date(), 18)}
                label="Fecha de nacimiento"
                inputVariant="outlined"
                name="birthdate"
                format="dd/MM/yyyy"
              />
            </Grid>
          </Fade>
          <Grid xs={4} item>
            <Field
              component={TextField}
              name="contactPhone"
              label="Teléfono de contacto (móvil)"
              variant="outlined"
              inputProps={{
                minLength: 9,
                maxLength: 9,
              }}
            />
          </Grid>
        </Grid>
        <Collapse in={isCompanySegment}>
          <Divider light />
          <Box my={6}>
            <Typography variant="subtitle2">DATOS DE LA EMPRESA</Typography>
          </Box>
          <Grid container component={Box} spacing={5}>
            <Grid xs={4} item>
              <FastField
                component={TextField}
                className={styles.upper}
                name="cif"
                label="CIF"
                variant="outlined"
                inputProps={{
                  maxLength: 9,
                  disabled: checkExistentCustomerValues('cif'),
                }}
              />
            </Grid>
            <Grid xs={4} item>
              <FastField
                component={TextField}
                name="companyName"
                label="Nombre de la empresa"
                variant="outlined"
                inputProps={{
                  readOnly: checkExistentCustomerValues('companyName'),
                }}
              />
            </Grid>
          </Grid>
        </Collapse>
      </fieldset>
    </Form>
  )
}

export default function CustomerInfoForm() {
  const styles = useStyles()
  const initialValues = useSelector(selectCustomerInfoFormValues)
  const isPostpaid = useSelector(selectIsPostpaid)
  return (
    <Paper elevation={5}>
      <Typography variant="h3" className="card-title">
        Datos del titular
      </Typography>
      <Divider light />
      <Box className={styles.root} px={6} pb={6}>
        <Formik
          validateOnMount
          validateOnBlur
          initialValues={initialValues}
          initialStatus={{}}
          validationSchema={customerInfoSchema(isPostpaid)}
          onSubmit={(values, formik) => {
            formik.setSubmitting(false)
            formik.validateForm()
          }}
          component={CustomerForm}
        />
      </Box>
    </Paper>
  )
}
